import { apiClient } from './apiService'

export interface Theme {
  id: string
  name: string
  value: string
}

export interface ThemeStats {
  name: string
  total: number
  statuses: Record<string, number>
}

export interface ThemeStatsResponse {
  success: boolean
  data: {
    themes: ThemeStats[]
    summary: {
      totalInitiatives: number
      withThemes: number
      withoutThemes: number
      uniqueThemes: number
    }
  }
  message: string
  error?: string
}

export interface ThemeResponse {
  success: boolean
  data: Theme[]
  total: number
  message: string
  error?: string
}

class ThemeService {
  private baseUrl = '/themes'

  /**
   * Get all themes
   */
  async getAllThemes(jql?: string): Promise<ThemeResponse> {
    try {
      const params = jql ? { jql } : {}
      const response = await apiClient.get<ThemeResponse>(this.baseUrl, { params })
      return response.data
    } catch (error) {
      console.error('Error fetching themes:', error)
      return {
        success: false,
        data: [],
        total: 0,
        message: 'Failed to fetch themes',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Search themes by name
   */
  async searchThemes(searchTerm: string): Promise<ThemeResponse> {
    try {
      if (!searchTerm.trim()) {
        return this.getAllThemes()
      }

      const response = await apiClient.get<ThemeResponse>(`${this.baseUrl}/search`, {
        params: { q: searchTerm }
      })
      return response.data
    } catch (error) {
      console.error(`Error searching themes for "${searchTerm}":`, error)
      return {
        success: false,
        data: [],
        total: 0,
        message: `Failed to search themes for "${searchTerm}"`,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get theme usage statistics
   */
  async getThemeStats(): Promise<ThemeStatsResponse> {
    try {
      const response = await apiClient.get<ThemeStatsResponse>(`${this.baseUrl}/stats`)
      return response.data
    } catch (error) {
      console.error('Error fetching theme statistics:', error)
      return {
        success: false,
        data: {
          themes: [],
          summary: {
            totalInitiatives: 0,
            withThemes: 0,
            withoutThemes: 0,
            uniqueThemes: 0
          }
        },
        message: 'Failed to fetch theme statistics',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Clear theme cache
   */
  async clearCache(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(`${this.baseUrl}/cache/clear`)
      return response.data
    } catch (error) {
      console.error('Error clearing theme cache:', error)
      return {
        success: false,
        message: 'Failed to clear theme cache'
      }
    }
  }

  /**
   * Filter themes by search term (client-side)
   */
  filterThemes(themes: Theme[], searchTerm: string): Theme[] {
    if (!searchTerm.trim()) {
      return themes
    }

    const term = searchTerm.toLowerCase()
    return themes.filter(theme => 
      theme.name.toLowerCase().includes(term) ||
      theme.id.includes(term) ||
      theme.value.toLowerCase().includes(term)
    )
  }

  /**
   * Sort themes by name
   */
  sortThemes(themes: Theme[], ascending: boolean = true): Theme[] {
    return [...themes].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name)
      return ascending ? comparison : -comparison
    })
  }

  /**
   * Get unique theme names from a list
   */
  getUniqueThemeNames(themes: Theme[]): string[] {
    const uniqueNames = new Set(themes.map(theme => theme.name))
    return Array.from(uniqueNames).sort()
  }

  /**
   * Group themes by first letter
   */
  groupThemesByLetter(themes: Theme[]): Record<string, Theme[]> {
    const grouped: Record<string, Theme[]> = {}
    
    themes.forEach(theme => {
      const firstLetter = theme.name.charAt(0).toUpperCase()
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = []
      }
      grouped[firstLetter].push(theme)
    })

    // Sort themes within each group
    Object.keys(grouped).forEach(letter => {
      grouped[letter] = this.sortThemes(grouped[letter])
    })

    return grouped
  }

  /**
   * Validate theme name
   */
  isValidThemeName(name: string): boolean {
    return name && name.trim().length > 0 && name.trim().length <= 100
  }

  /**
   * Normalize theme name for comparison
   */
  normalizeThemeName(name: string): string {
    return name.trim().toLowerCase().replace(/[^a-z0-9]/g, '_')
  }

  /**
   * Check if two themes are the same
   */
  areThemesEqual(theme1: Theme, theme2: Theme): boolean {
    return this.normalizeThemeName(theme1.name) === this.normalizeThemeName(theme2.name)
  }

  /**
   * Get theme color based on name (for UI consistency)
   */
  getThemeColor(themeName: string): string {
    // Simple hash-based color generation for consistent theme colors
    let hash = 0
    for (let i = 0; i < themeName.length; i++) {
      hash = themeName.charCodeAt(i) + ((hash << 5) - hash)
    }
    
    const hue = Math.abs(hash) % 360
    return `hsl(${hue}, 65%, 50%)`
  }

  /**
   * Format theme name for display
   */
  formatThemeForDisplay(theme: Theme): string {
    if (!theme.name) return 'No Theme'
    
    // Capitalize first letter of each word
    return theme.name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }
}

export const themeService = new ThemeService()
export default themeService
